import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  api,
  ApiError,
  type DietOverview,
  type DietFoodSource,
  type DietMealSuggestion,
} from "../../lib/api";

const CAT_LABEL: Record<string, string> = {
  protein: "Protein",
  carb: "Healthy carbs",
  fat: "Healthy fats",
  veg: "Vegetables",
  workout_carb: "Workout carbs",
};

export default function DietGuide() {
  const [ov, setOv] = useState<DietOverview | null>(null);
  const [slot, setSlot] = useState<number>(1);
  const [busy, setBusy] = useState(false);
  const [sugg, setSugg] = useState<DietMealSuggestion[] | null>(null);
  const [suggErr, setSuggErr] = useState<string | null>(null);

  useEffect(() => {
    api.diet.overview().then(setOv).catch(() => setOv(null));
  }, []);

  const activeMeals =
    ov?.phases.find((p) => p.id === ov.plan?.active_phase_id)?.meals ?? ov?.phases[0]?.meals ?? [];

  async function suggest() {
    setBusy(true);
    setSugg(null);
    setSuggErr(null);
    try {
      const r = await api.diet.suggestMeal(slot);
      setSugg(r.suggestions);
    } catch (e) {
      setSuggErr(
        e instanceof ApiError && e.status === 503
          ? "Meal ideas need the Anthropic key set on the server."
          : e instanceof ApiError
            ? `API ${e.status}`
            : (e as Error).message,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl animate-fade-in space-y-6">
      <div>
        <h2 className="section-title mb-1">How this works</h2>
        <p className="text-ink-subtle text-small">
          Your RP-style plan tracks <b>grams of the nutrient</b> (protein / carb / fat) plus veggies
          in cups — not total food weight. Hit each meal's amounts from the approved foods and the
          rest takes care of itself.
        </p>
      </div>

      <Section title="Training-day types">
        Only <b>carbs</b> change with how hard you trained — protein, fat and veg stay the same. Pick
        the day type on the <Link to="/diet" className="text-accent">Today</Link> tab.
        <ul className="mt-2 space-y-1 text-sm text-ink-muted">
          <li><b>Rest</b> — no weight training that day.</li>
          <li><b>Light</b> — short (&lt;1h) or low-volume sessions (≤10 work sets).</li>
          <li><b>Moderate</b> — 1–2h, 10–25 work sets; tired but "could do a bit more".</li>
          <li><b>Hard</b> — &gt;2h or 26+ work sets; high-volume legs, left wrecked.</li>
        </ul>
      </Section>

      <Section title="Phases & the weekly loop">
        You progress through sheets: <b>Base → Fat Loss 1 → 2 → 3 → Maintenance → New Base</b>. Weigh
        in 2–3× a week, first thing, on an empty stomach. The{" "}
        <Link to="/diet/checkin" className="text-accent">Check-in</Link> tab averages your weight,
        compares your weekly rate to the RP target, and tells you to <b>hold</b>, <b>advance</b>, or{" "}
        <b>step back</b> a sheet. Don't run a cut longer than ~3 months without a maintenance break.
      </Section>

      <Section title="How many meals?">
        Eat your day's totals across however many meals suit you — <b>3 or 4 is completely fine</b>.
        Fewer meals just means bigger meals; you're hitting the same daily numbers. Set your meal
        count on the <Link to="/diet/template" className="text-accent">Template</Link> tab (delete
        rows and fold their macros into the meals you keep).
      </Section>

      <Section title="Building a meal">
        Macros are grams of the <i>nutrient</i>: e.g. 100g of sweet potato only yields ~25g of carbs.
        Roughly, one serving ≈ <b>40g protein</b>, <b>15g fat</b> (1 tbsp oil), <b>30g carbs</b>, or{" "}
        <b>1 cup veg</b> (a small handful). Combine a protein + carb + fat + veg source to hit a
        meal's targets.
      </Section>

      {/* Food types */}
      <Section title="Approved food types">
        {ov ? (
          <div className="space-y-3">
            {(["protein", "carb", "fat", "veg", "workout_carb"] as const).map((cat) => {
              const items = ov.food_sources.filter((f) => f.category === cat);
              if (!items.length) return null;
              return <FoodCat key={cat} cat={cat} items={items} />;
            })}
          </div>
        ) : (
          <p className="muted text-sm">Loading your food lists…</p>
        )}
      </Section>

      {/* AI recipe suggestions */}
      <Section title="Meal ideas">
        <p className="text-sm text-ink-muted">
          Get meal ideas that hit a specific meal's targets using your approved foods.
        </p>
        <div className="mt-2 flex gap-2">
          <select value={slot} onChange={(e) => setSlot(Number(e.target.value))} className="input flex-1">
            {activeMeals.map((m) => (
              <option key={m.meal_index} value={m.meal_index}>
                {m.name ?? `Meal ${m.meal_index}`}
              </option>
            ))}
          </select>
          <button type="button" className="btn-accent shrink-0 px-4" onClick={suggest} disabled={busy || activeMeals.length === 0}>
            {busy ? "Thinking…" : "Suggest"}
          </button>
        </div>
        {suggErr && <p className="mt-2 text-sm text-loss">{suggErr}</p>}
        {sugg && (
          <div className="mt-3 space-y-2">
            {sugg.length === 0 && <p className="muted text-sm">No ideas came back — try again.</p>}
            {sugg.map((s, i) => (
              <div key={i} className="rounded-md border border-ink-line bg-canvas p-3">
                <div className="text-sm font-medium text-ink">{s.name}</div>
                <div className="mt-0.5 text-sm text-ink-muted">{s.items}</div>
                {s.note && <div className="mt-1 text-xs text-ink-subtle">{s.note}</div>}
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="card-pad">
      <h3 className="section-title mb-2">{title}</h3>
      <div className="text-[15px] leading-relaxed text-ink-muted">{children}</div>
    </section>
  );
}

function FoodCat({ cat, items }: { cat: string; items: DietFoodSource[] }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">{CAT_LABEL[cat]}</div>
      <div className="mt-0.5 text-sm text-ink-muted">{items.map((it) => it.name).join(" · ")}</div>
      {items[0]?.serving_note && <div className="text-xs text-ink-subtle">serving ≈ {items[0].serving_note}</div>}
    </div>
  );
}
